"use client";

import { useState } from "react";
import Image from "next/image";
import NextLink from "next/link";
import { useRouter } from "next/router";
import {
  Alert,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { decode } from "jsonwebtoken";
import { FaExclamationCircle } from "react-icons/fa";
import * as Yup from "yup";
import { OrangeButton } from "@/lib/chakra/components";
import { IUser } from "@/lib/interfaces";
import { setSkip } from "@/lib/reducers/apiData";
import { setAccessToken, setUser } from "@/lib/reducers/user";
import {
  useLazyGetClientsQuery,
  useLazyGetUserDetailsQuery,
  useLoginMutation,
  useLogoutMutation,
} from "@/lib/services/appApi";
import { AppPayload } from "@/lib/types";
import { useAppDispatch, useAppSelector } from "../../lib/hooks";
import { GetLogo } from "../ClientBased";

const SignIn = () => {
  const maintenance = false;

  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const dispatch = useAppDispatch();
  const [loginMutation] = useLoginMutation();
  const [userDetailsQuery] = useLazyGetUserDetailsQuery();
  const [getClients] = useLazyGetClientsQuery();
  const [logout] = useLogoutMutation();

  interface Values {
    email: string;
    password: string;
  }

  const text = useColorModeValue("#232323", "#ffffff");

  const signInFormik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        // .email("Invalid email address")
        .required("Email is required."),
      password: Yup.string()
        .required("Password is required.")
        .min(3, "Password is too short - should be 3 chars minimum."),
    }),
    onSubmit: async ({ email, password }: Values) => {
      let cid = router?.query?.cid;
      let qid = router?.query?.qid;

      setLoading(true);

      try {
        const { accessToken } = await loginMutation({
          email,
          password,
        }).unwrap();

        const decodedToken: AppPayload = decode(accessToken) as AppPayload;
        if (decodedToken?.token) {
          const { data: userData } = await userDetailsQuery({
            accessToken: decodedToken.token,
          });
          const { data: clientsData } = await getClients({
            accessToken: decodedToken.token,
          });

          if (clientsData?.data?.length === 0) {
            //remove token - logout
            setLoading(false);
            setErrorMessage("Cannot login. Please contact your administrator.");
            await logout();
            return;
          }
          if (!userData?.data || userData?.data?.length === 0) {
            console.error("No user data returned");
            return;
          }

          if (userData?.status === "success") {
            setErrorMessage("");

            dispatch(
              setUser({
                user: userData.data as IUser,
              })
            );
            dispatch(
              setAccessToken({
                accessToken,
              })
            );
          }
        }
        dispatch(setSkip({ skip: false }));

        //check if redirect back to qr?
        if (cid && qid) {
          router.push(`/qr/${cid}/${qid}`);
        }

        const pathname = localStorage.getItem("pathname");
        // //last visited page
        if (pathname != null) {
          router.push(pathname);
        } else {
          router.push("/dashboard");
        }
      } catch (err) {
        setLoading(false);
        setErrorMessage("Invalid credentials");
      }
    },
  });
  const [show, setShow] = useState<boolean>(false);
  const handleClick = () => setShow(!show);

  return (
    <>
      <Flex justify={"flex-start"}>
        <NextLink href="/login" passHref>
          <GetLogo />
        </NextLink>
      </Flex>
      <Text mt={5} color="#8F8F8F">
        Please enter your details
      </Text>
      <form
        style={{
          width: "100%",
          display: maintenance ? "none" : "block",
        }}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          signInFormik.handleSubmit(e);
        }}
      >
        <FormControl
          my={6}
          id="email"
          isInvalid={
            signInFormik.errors.email !== "" &&
            signInFormik.errors.email !== undefined
          }
        >
          <FormLabel fontWeight="bold" htmlFor="email" fontSize="sm">
            Email
          </FormLabel>
          <Input
            autoFocus={true}
            value={signInFormik.values.email}
            onChange={signInFormik.handleChange}
            name="email"
            disabled={loading}
            type="email"
            placeholder="Email"
            autoComplete="email"
            fontSize={"md"}
          />
          <FormErrorMessage>{signInFormik.errors.email}</FormErrorMessage>
        </FormControl>

        <FormControl
          id="password"
          isInvalid={
            signInFormik.errors.password !== "" &&
            signInFormik.errors.password !== undefined
          }
        >
          <FormLabel fontWeight="bold" htmlFor="email" fontSize="sm">
            Password
          </FormLabel>
          <InputGroup>
            <Input
              autoComplete="current-password"
              disabled={loading}
              value={signInFormik.values.password}
              onChange={signInFormik.handleChange}
              name="password"
              pr="4.5rem"
              type={show ? "text" : "password"}
              placeholder="Enter password"
              fontSize={"md"}
            />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={handleClick}>
                {show ? "Hide" : "Show"}
              </Button>
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{signInFormik.errors.password}</FormErrorMessage>
        </FormControl>

        <Flex my={5} w="100%" justifyContent={"flex-end"}>
          <Link
            as={NextLink}
            href="/passwordReset"
            color={text}
            fontWeight={"bold"}
          >
            Forgot password
          </Link>
        </Flex>
        <Alert
          my={5}
          status="error"
          display={errorMessage === "" ? "none" : "block"}
        >
          <HStack>
            <Box pr={1} color={"red.600"}>
              <FaExclamationCircle />
            </Box>
            <Text fontSize={"md"}>{errorMessage}</Text>
          </HStack>
        </Alert>

        <OrangeButton
          type="submit"
          size="lg"
          fontSize="md"
          isLoading={loading}
          w="100%"
        >
          Log In
        </OrangeButton>

        <Flex my={5} w="100%" justifyContent={"center"} display="none">
          <Text color="#8F8F8F"> Don’t have an account?</Text>

          <Link
            href="/signup"
            passHref
            as={NextLink}
            ml={1}
            color={text}
            fontWeight={"bold"}
          >
            Sign Up
          </Link>
        </Flex>
      </form>
    </>
  );
};

export default SignIn;
